import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { sectors } from "@/data/products";
import { Mail, Phone, MapPin, MessageCircle, Send, Globe, ArrowUpRight, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phones = [
  { num: "+91 98189 88093", wa: "919818988093" },
  { num: "+91 79826 50076", wa: "917982650076" },
  { num: "+91 83838 13893", wa: "918383813893" },
];

const team = [
  { name: "Sandeep Rajat", role: "CEO · Founder", phone: "+91 98189 88093" },
  { name: "Om Prakash", role: "Operations Director", phone: "+91 83838 13893" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    country: "", product_interest: "", quantity: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received. We'll respond within 12 business hours.");
      setForm({ name: "", company: "", email: "", phone: "", country: "", product_interest: "", quantity: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#F9F8F6]">
      {/* LUXURY HERO */}
      <section className="relative h-[92vh] min-h-[640px] flex items-end overflow-hidden bg-[#0D1A12]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=2400&q=90"
            alt="Cinematic global port — golden hour"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D1A12]/50 via-[#0D1A12]/30 to-[#0D1A12]/95" />
        </div>

        <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-16 text-[#F9F8F6]">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[10px] uppercase tracking-[0.5em] gold-text mb-5">Connect · Collaborate · Trade</p>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-[112px] font-light tracking-tighter leading-[0.95]">
              Let's build<br/><em className="gold-text not-italic">something global.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl font-light max-w-2xl opacity-90 leading-relaxed">
              Send your requirements and our trade desk will respond within twelve business hours with a competitive
              commercial quotation and a complete documentation preview.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#enquiry-form" className="inline-flex items-center gap-3 rounded-full bg-[#D4AF37] text-[#1A1B1A] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#F9F8F6] transition-colors">
                Send an Enquiry <ArrowUpRight size={14} />
              </a>
              <a href={`https://wa.me/${phones[0].wa}?text=Hello%20AR%20Overseas%20-%20I%27d%20like%20to%20discuss%20an%20export%20inquiry`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/60 text-[#F9F8F6] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#D4AF37]/15">
                <MessageCircle size={14} /> WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* QUICK CONNECT BAR */}
      <section className="bg-[#0D1A12] text-[#F9F8F6] py-10 border-b border-[#D4AF37]/20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Clock, label: "Quote Response", value: "12 hrs", sub: "Business hours" },
            { icon: Globe, label: "Markets Served", value: "40+", sub: "Six continents" },
            { icon: Phone, label: "Direct Line", value: phones[0].num, sub: "Trade desk · Mon–Sat" },
            { icon: Mail, label: "Email", value: "info@aroverseasexports.com", sub: "Response within 12h" },
          ].map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
                <Icon size={16} />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{label}</p>
                <p className="font-serif text-xl mt-1">{value}</p>
                <p className="text-[11px] opacity-60">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN: form + info side */}
      <section id="enquiry-form" className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — info */}
          <aside className="lg:col-span-5 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-3">Visit · Call · Connect</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
                The trade desk is <em className="text-[#B85C38]">always open.</em>
              </h2>
            </div>

            <div className="rounded-3xl bg-[#0D1A12] text-[#F9F8F6] p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full holographic blur-3xl opacity-50" />
              <div className="relative">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Office</p>
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5 text-[#D4AF37]" />
                  <p className="font-light leading-relaxed">
                    AR Overseas Exports Pvt. Ltd.<br/>
                    166, Ram Vihar, Delhi · INDIA<br/>
                    Zip Code — 110092
                  </p>
                </div>

                <div className="gold-divider my-6" />

                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Phone · WhatsApp</p>
                <div className="space-y-2">
                  {phones.map((p) => (
                    <a key={p.num} href={`https://wa.me/${p.wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-[#D4AF37] transition-colors">
                      <Phone size={14} className="text-[#D4AF37]" />
                      <span>{p.num}</span>
                    </a>
                  ))}
                </div>

                <div className="gold-divider my-6" />

                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Email · Web</p>
                <div className="space-y-2">
                  <a href="mailto:info@aroverseasexports.com" className="flex items-center gap-3 hover:text-[#D4AF37]">
                    <Mail size={14} className="text-[#D4AF37]" />
                    <span>info@aroverseasexports.com</span>
                  </a>
                  <a href="https://www.aroverseasexports.com" className="flex items-center gap-3 hover:text-[#D4AF37]">
                    <Globe size={14} className="text-[#D4AF37]" />
                    <span>www.aroverseasexports.com</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Leadership cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {team.map((t) => (
                <div key={t.name} className="rounded-2xl border border-[#EAE6DF] bg-white p-5">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{t.role}</p>
                  <p className="font-serif text-2xl text-[#2C4532] mt-1">{t.name}</p>
                  <p className="text-sm text-[#4A4D4A] mt-2">{t.phone}</p>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="rounded-3xl overflow-hidden border border-[#EAE6DF] h-[260px]">
              <iframe
                title="AR Overseas Location"
                width="100%"
                height="100%"
                loading="lazy"
                src="https://www.google.com/maps?q=Ram+Vihar+New+Delhi+110092&output=embed"
                style={{ border: 0 }}
              />
            </div>
          </aside>

          {/* RIGHT — form */}
          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7">
            <div className="rounded-3xl border border-[#EAE6DF] bg-white p-8 md:p-12 shadow-[0_24px_60px_rgba(13,26,18,0.08)]">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B85C38]">Request a Quote</p>
              <h3 className="font-serif text-3xl md:text-4xl font-light tracking-tight mt-2">Tell us what you need.</h3>
              <p className="text-[#4A4D4A] mt-2 font-light">
                We respond within twelve business hours with pricing, documentation preview and reference samples on request.
              </p>

              <div className="gold-divider my-8" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name *" testid="form-name"><Input required value={form.name} onChange={(e) => onChange("name", e.target.value)} className="h-12 rounded-xl border-[#EAE6DF]" /></Field>
                <Field label="Company" testid="form-company"><Input value={form.company} onChange={(e) => onChange("company", e.target.value)} className="h-12 rounded-xl border-[#EAE6DF]" /></Field>
                <Field label="Email *" testid="form-email"><Input type="email" required value={form.email} onChange={(e) => onChange("email", e.target.value)} className="h-12 rounded-xl border-[#EAE6DF]" /></Field>
                <Field label="Phone / WhatsApp" testid="form-phone"><Input value={form.phone} onChange={(e) => onChange("phone", e.target.value)} className="h-12 rounded-xl border-[#EAE6DF]" /></Field>
                <Field label="Country" testid="form-country"><Input value={form.country} onChange={(e) => onChange("country", e.target.value)} className="h-12 rounded-xl border-[#EAE6DF]" /></Field>
                <Field label="Quantity / Container" testid="form-quantity"><Input value={form.quantity} onChange={(e) => onChange("quantity", e.target.value)} placeholder="e.g. 1x40' HC or 20 MT" className="h-12 rounded-xl border-[#EAE6DF]" /></Field>
              </div>

              <div className="mt-5">
                <Field label="Product Interest" testid="form-product">
                  <select
                    value={form.product_interest}
                    onChange={(e) => onChange("product_interest", e.target.value)}
                    className="w-full h-12 rounded-xl border border-[#EAE6DF] bg-white px-3 text-sm"
                  >
                    <option value="">Select a sector (optional)</option>
                    {sectors.map((s) => (
                      <option key={s.slug} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Message *" testid="form-message">
                  <Textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => onChange("message", e.target.value)}
                    placeholder="Tell us about your requirement — product grade, destination port, Incoterm preference, timeline and any special certifications needed (EU Organic, Halal, etc.)."
                    className="rounded-xl border-[#EAE6DF]"
                  />
                </Field>
              </div>

              <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
                <Button type="submit" disabled={loading} data-testid="form-submit" className="rounded-full bg-[#2C4532] text-[#F9F8F6] hover:bg-[#1F3124] h-12 px-8 text-xs uppercase tracking-[0.25em]">
                  <Send size={14} className="mr-2" />{loading ? "Sending…" : "Transmit Enquiry"}
                </Button>
                <p className="text-xs text-[#4A4D4A] font-light">
                  We respond within 12 business hours · ✱ Required
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* SHORT FAQ */}
      <section className="pb-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-3 text-center">Frequently Asked</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light tracking-tight text-center">A few of the questions buyers usually ask.</h3>
          <div className="mt-12 space-y-4">
            {[
              { q: "What's the typical lead time?", a: "Quotation within 12 business hours. Sample dispatch within 3–5 days. Full container shipments are typically ready in 10–25 days from confirmed order, depending on product and destination compliance." },
              { q: "Which Incoterms do you support?", a: "EXW, FOB, CFR, CIF, DDP — pricing with or without inland handling. We ship from Mumbai, JNPT, Mundra, Pipavav, Chennai, Kolkata and Kandla." },
              { q: "Do you handle private label and custom packaging?", a: "Yes. OEM and private-label programs available across most sectors — including biodegradable, halal-certified, Arabic-bilingual, and FSC paper packaging compliant with destination markets." },
              { q: "Which certifications can you provide?", a: "APEDA, ISO 22000, HACCP, GlobalG.A.P., EU Organic (EC 848/2018), USDA NOP, JAS, Halal, GI tag, ASTM, EN, FSANZ, GACC, SFA — destination-specific docs handled in-house." },
            ].map((f, i) => (
              <details key={i} className="group rounded-2xl border border-[#EAE6DF] bg-white p-6 hover:border-[#D4AF37] transition-colors">
                <summary className="cursor-pointer flex items-center justify-between font-serif text-xl text-[#2C4532]">
                  {f.q}
                  <span className="text-[#D4AF37] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#4A4D4A] font-light leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-[0.25em] text-[#4A4D4A] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
